import _ from 'lodash'
import { withDependencies, named, optional } from '@wix/thunderbolt-ioc'
import {
	PageFeatureConfigSymbol,
	SiteFeatureConfigSymbol,
	IPageWillMountHandler,
	IPropsStore,
	Props,
	ICompEventsRegistrar,
	CompEventsRegistrarSym,
	ViewMode,
	ViewModeSym,
	IMultilingual,
	Language,
	ExperimentsSymbol,
	Experiments,
} from '@wix/thunderbolt-symbols'
import { LanguageSelectorPageConfig, LanguageSelectorSiteConfig, ItemFormat, IconType, DisplayMode } from './types'
import { name } from './symbols'
import { MultilingualSymbol } from 'feature-multilingual'
import { utils } from 'santa-multilingual/dist/languages' // eslint-disable-line no-restricted-syntax
import { LanguageSelectorProps, LanguageSelectorItem } from '@wix/thunderbolt-components'

function calculateItemText(language: Language, itemFormat: ItemFormat, displayMode: DisplayMode, isMobile: boolean) {
	if (itemFormat === 'shortName') {
		return language.languageCode.toUpperCase()
	} else if (itemFormat === 'fullName') {
		return language.name
	} else if (itemFormat === 'fullLocalizedName') {
		return language.localizedName
	} else if (itemFormat === 'iconOnly' && displayMode !== 'horizontal' && isMobile) {
		return language.name
	}
}

const languageSelectorFactory = (
	pageFeatureConfig: LanguageSelectorPageConfig,
	siteFeatureConfig: LanguageSelectorSiteConfig,
	props: IPropsStore,
	compEventsRegistrar: ICompEventsRegistrar,
	viewMode: ViewMode,
	experiments: Experiments,
	multilingual?: IMultilingual
): IPageWillMountHandler => {
	const sortByOriginalLanguage = (language: LanguageSelectorItem) =>
		language.value === siteFeatureConfig.originalLanguageCode ? -1 : 1

	return {
		async pageWillMount() {
			if (!multilingual) {
				return
			}
			const getItems = (
				iconType: IconType,
				itemFormat: ItemFormat,
				languagesOrder: Array<string>,
				displayMode: DisplayMode
			): Array<LanguageSelectorItem> => {
				const isPng = experiments['specs.thunderbolt.multilingualFlagsPng']
				const flagIconFn = isPng ? utils.getFlagIconPng : utils.getFlagIconUrl
				const flagSrcSetFn = isPng ? utils.getFlagIconSrcSet : () => undefined

				const { flagsUrl } = siteFeatureConfig

				const languages = _(multilingual.siteLanguages)
					.map((language) => {
						return {
							text: calculateItemText(language, itemFormat, displayMode, viewMode === 'mobile'),
							value: language.languageCode,
							selected: language.languageCode === multilingual.currentLanguage.languageCode,
							label: language.name,
							flag:
								iconType && iconType !== 'none'
									? flagIconFn(flagsUrl, iconType, language.countryCode)
									: undefined,
							flagSrcSet: flagSrcSetFn(flagsUrl, iconType, language.countryCode),
						}
					})
					.sortBy((language) =>
						languagesOrder.length
							? languagesOrder.indexOf(language.value)
							: sortByOriginalLanguage(language)
					)
					.value()

				return languages
			}

			const newProps = pageFeatureConfig.componentsMetaData.reduce<
				Record<string, Partial<LanguageSelectorProps>>
			>((acc, { id, iconType, itemFormat, languagesOrder, displayMode }) => {
				compEventsRegistrar.register(id, 'onChange', multilingual.setCurrentLanguage)
				acc[id] = {
					items: getItems(iconType, itemFormat, languagesOrder, displayMode),
				}
				return acc
			}, {})
			props.update(newProps)
		},
	}
}

export const LanguageSelector = withDependencies(
	[
		named(PageFeatureConfigSymbol, name),
		named(SiteFeatureConfigSymbol, name),
		Props,
		CompEventsRegistrarSym,
		ViewModeSym,
		ExperimentsSymbol,
		optional(MultilingualSymbol),
	],
	languageSelectorFactory
)
